import type { ClientGateway } from "@kex/api";

import {
  type CodeExpr,
  type ErrorCode,
  apiErrors,
} from "@kex-resources/api-errors";

import type { QueryApiError, SubmitFormError } from "../types";

export const isErrorResponse = (
  value: unknown,
): value is ClientGateway.ErrorResponse => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if ("id" in value && typeof value.id !== "string") {
    return false;
  }
  if ("timestamp" in value && typeof value.timestamp !== "string") {
    return false;
  }
  if ("customCode" in value && typeof value.customCode !== "string") {
    return false;
  }
  if ("code" in value && typeof value.code !== "string") {
    return false;
  }
  return !("message" in value && typeof value.message !== "string");
};

export const isErrorCode = (value: unknown): value is ErrorCode => {
  if (typeof value !== "string") {
    return false;
  }
  return Object.keys(apiErrors).includes(value);
};

export const isExactErrorCode = <E extends ErrorCode>(
  errors: E[],
  code: string,
): code is E => {
  return errors.includes(code as E);
};

export const isQueryApiError = (
  e: unknown,
): e is QueryApiError<CodeExpr, ErrorCode> => {
  if (typeof e !== "object" || e === null) {
    return false;
  }
  return "code" in e && typeof e.code === "string";
};

export const isSubmitFormError = <C extends ErrorCode, F extends string>(
  e: unknown,
  validCodes: C[],
): e is SubmitFormError<CodeExpr, C, F> => {
  if (typeof e !== "object" || e === null) {
    return false;
  }
  const hasCode = "code" in e;
  if (!hasCode || !isErrorCode(e.code)) {
    return false;
  }
  if (!isExactErrorCode(validCodes, e.code)) {
    return false;
  }
  return "fieldErrorMessage" in e && "fieldNames" in e;
};
